import React, { useState } from "react";
import { Copy, Check, Key, Database, Server, Globe, CreditCard, Code, Smartphone, Terminal, ClipboardList } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const C = ({ v }) => {
  const [ok, setOk] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(v); setOk(true); toast.success("Copiato!"); setTimeout(() => setOk(false), 1500); }}
      className="inline-flex items-center gap-1.5 bg-[#1A1A2E] text-emerald-400 font-mono text-sm px-3 py-1.5 rounded-lg hover:bg-[#2A2A4E] transition-colors cursor-pointer group max-w-full"
    >
      <span className="break-words text-left">{v}</span>
      {ok ? <Check className="h-3.5 w-3.5 text-emerald-400 shrink-0" /> : <Copy className="h-3.5 w-3.5 text-white/40 group-hover:text-white shrink-0" />}
    </button>
  );
};

const Row = ({ label, value }) => (
  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 py-2 border-b border-slate-100 last:border-0">
    <span className="text-xs uppercase tracking-widest text-[#6A564A] font-bold w-40 shrink-0">{label}</span>
    <C v={value} />
  </div>
);

const Sec = ({ icon: Icon, title, id, children }) => (
  <div className="bg-white border-2 border-[#09090B] rounded-2xl p-6 shadow-[4px_4px_0_#09090B]" id={id}>
    <div className="flex items-center gap-2 mb-4">
      <div className="w-8 h-8 rounded-lg bg-[#FF4A00] flex items-center justify-center">
        <Icon className="h-4 w-4 text-white" />
      </div>
      <h3 className="font-heading text-lg font-black text-[#09090B]">{title}</h3>
    </div>
    {children}
  </div>
);

export default function HandoffPage() {
  const appUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  const copyAll = () => {
    const all = `
=== BOOKEASY — DATI COMPLETI ===

URL APP: ${appUrl}
DOCUMENTAZIONE: ${appUrl}/docs
PANNELLO ADMIN: ${appUrl}/admin

--- ACCOUNT ADMIN ---
Email: titolare@bookeasy.it
Password: BookEasy2026!

--- ACCOUNT TEST CLIENTE ---
Email: danielenicotra@test.com
Password: Test1234!

--- ACCOUNT TEST 2 ---
Email: senesecristian@icloud.com
Password: BookEasy2026!

--- DATABASE ---
MongoDB: mongodb://localhost:27017
Database: test_database

--- CHIAVI API (backend/.env) ---
STRIPE_API_KEY=sk_test_emergent
EMERGENT_LLM_KEY=sk-emergent-156F01d2bF3A2Cd18F
ADMIN_EMAIL=titolare@bookeasy.it

--- SATISPAY ---
Destinatario: Cristian Senese
Numero: +393398403932
Commissione piattaforma: 1 euro

--- STACK ---
Frontend: React 18 + TailwindCSS + Shadcn/UI
Backend: FastAPI (Python) - server.py (~3270 righe)
Database: MongoDB (motor async)
Pagamenti: Stripe + Satispay
Mobile: Capacitor (iOS/Android) + PWA
Auth: Google OAuth + Apple Sign In + Email
Storage: Emergent Object Storage

--- PAGINE FRONTEND ---
/ — Landing page
/admin — Pannello admin (god-mode: entra in ogni profilo)
/docs — Questa documentazione
/dashboard — Redirect automatico per ruolo
/customer — Dashboard cliente
/business — Dashboard attivita
/business/register — Registrazione attivita
/book/:businessId — Pagina prenotazione

--- API ADMIN (GOD-MODE) ---
GET /api/admin/stats — Statistiche piattaforma
GET /api/admin/users — Lista tutti utenti
GET /api/admin/users/{id}/bookings — Prenotazioni di un utente
PUT /api/admin/users/{id}/edit — Modifica profilo utente (name, email, phone, role)
PUT /api/admin/users/{id}/ban — Banna/sbanna utente
DELETE /api/admin/users/{id} — Elimina utente + tutti i dati
GET /api/admin/businesses/{id}/bookings — Prenotazioni di un'attivita
PUT /api/admin/bookings/{id}/status — Forza stato prenotazione (confirmed/cancelled/completed)
DELETE /api/admin/businesses/{id} — Elimina attivita
GET /api/admin/transactions — Tutte le transazioni

--- COMANDI AVVIO ---
Backend: cd /app/backend && pip install -r requirements.txt && uvicorn server:app --host 0.0.0.0 --port 8001 --reload
Frontend: cd /app/frontend && yarn install && yarn start
Mobile iOS: cd /app/frontend && npx cap sync && npx cap open ios
Mobile Android: cd /app/frontend && npx cap sync && npx cap open android

--- PER PRODUZIONE ---
1. Dominio (es. bookeasy.it)
2. Chiavi Stripe LIVE da dashboard.stripe.com
3. Google OAuth in Google Cloud Console
4. Apple Sign In su developer.apple.com
5. Server cloud (VPS/AWS/Hetzner)
6. MongoDB Atlas o server dedicato
7. SSL/HTTPS (Let's Encrypt)
8. Chiave Resend per email (resend.com)
9. Pubblicare App Store / Play Store

--- FILE PRINCIPALI ---
/app/backend/server.py — API FastAPI completa
/app/backend/.env — Chiavi API
/app/frontend/src/App.js — Router principale
/app/frontend/src/pages/ — Tutte le pagine
/app/frontend/src/components/ — Componenti riutilizzabili
/app/frontend/src/contexts/ — Auth + Lingua
/app/HANDOFF_PROFESSIONISTA.md — Doc tecnica completa
/app/MOBILE_APP_GUIDE.md — Guida pubblicazione app
    `.trim();
    navigator.clipboard.writeText(all);
    toast.success("Tutto copiato negli appunti!");
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-10 px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="text-center mb-6">
          <h1 className="font-heading text-3xl sm:text-4xl font-black text-[#09090B]">
            Book<span className="text-[#FF4A00]">Easy</span> — Documentazione
          </h1>
          <p className="text-[#6A564A] mt-1 text-sm">Clicca su qualsiasi valore per copiarlo</p>
          <Button
            onClick={copyAll}
            className="mt-4 bg-[#FF4A00] text-white font-bold rounded-xl px-6 py-3 text-base hover:bg-[#E04400] border-2 border-[#09090B] shadow-[3px_3px_0_#09090B] hover:shadow-none hover:translate-x-[3px] hover:translate-y-[3px] transition-all"
            data-testid="copy-all-btn"
          >
            <ClipboardList className="h-5 w-5 mr-2" />
            COPIA TUTTO IN UN CLICK
          </Button>
        </div>

        {/* CREDENZIALI */}
        <Sec icon={Key} title="Credenziali di Accesso" id="credenziali">
          <div className="space-y-1">
            <Row label="URL App" value={appUrl} />
            <Row label="Pagina Admin" value={`${appUrl}/admin`} />
            <Row label="Questa pagina" value={`${appUrl}/docs`} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-2">
              <p className="text-xs uppercase tracking-widest text-amber-700 font-bold">Admin</p>
              <Row label="Email" value="titolare@bookeasy.it" />
              <Row label="Password" value="BookEasy2026!" />
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 space-y-2">
              <p className="text-xs uppercase tracking-widest text-emerald-700 font-bold">Cliente Test</p>
              <Row label="Email" value="danielenicotra@test.com" />
              <Row label="Password" value="Test1234!" />
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-2">
              <p className="text-xs uppercase tracking-widest text-blue-700 font-bold">Cliente Test 2</p>
              <Row label="Email" value="senesecristian@icloud.com" />
              <Row label="Password" value="BookEasy2026!" />
            </div>
          </div>
        </Sec>

        {/* CHIAVI API */}
        <Sec icon={Server} title="Chiavi API (backend/.env)" id="api-keys">
          <div className="space-y-1">
            <Row label="Stripe" value="sk_test_emergent" />
            <Row label="LLM Key" value="sk-emergent-156F01d2bF3A2Cd18F" />
            <Row label="Admin Email" value="titolare@bookeasy.it" />
            <Row label="MongoDB" value="mongodb://localhost:27017" />
            <Row label="DB Name" value="test_database" />
          </div>
        </Sec>

        {/* SATISPAY */}
        <Sec icon={CreditCard} title="Pagamenti Satispay" id="satispay">
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-2">
            <p className="text-xs uppercase tracking-widest text-red-600 font-bold">Commissione Piattaforma (1 euro)</p>
            <Row label="Destinatario" value="Cristian Senese" />
            <Row label="Numero" value="+393398403932" />
          </div>
          <div className="mt-3 text-sm space-y-1">
            <p className="font-bold text-[#09090B]">Regole per Categoria:</p>
            <p className="text-xs text-[#6A564A]"><strong className="text-emerald-600">GRATIS (0 euro):</strong> avvocato, commercialista, notaio, architetto, ingegnere</p>
            <p className="text-xs text-[#6A564A]"><strong className="text-blue-600">SOLO 1 euro:</strong> medico, dentista, clinica, psicologo, fisioterapista, oculista, veterinario</p>
            <p className="text-xs text-[#6A564A]"><strong className="text-amber-600">CAUZIONE + 1 euro:</strong> tutte le altre categorie</p>
          </div>
        </Sec>

        {/* COMANDI */}
        <Sec icon={Terminal} title="Comandi per Avviare" id="comandi">
          <div className="space-y-3">
            <div>
              <p className="text-xs uppercase tracking-widest text-[#6A564A] font-bold mb-1">Backend</p>
              <C v="cd /app/backend && pip install -r requirements.txt && uvicorn server:app --host 0.0.0.0 --port 8001 --reload" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-[#6A564A] font-bold mb-1">Frontend</p>
              <C v="cd /app/frontend && yarn install && yarn start" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-[#6A564A] font-bold mb-1">Mobile iOS</p>
              <C v="cd /app/frontend && npx cap sync && npx cap open ios" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-[#6A564A] font-bold mb-1">Mobile Android</p>
              <C v="cd /app/frontend && npx cap sync && npx cap open android" />
            </div>
          </div>
        </Sec>

        {/* DATABASE */}
        <Sec icon={Database} title="Database — Collezioni" id="database">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b-2 border-[#09090B]"><th className="text-left py-2 px-3">Collezione</th><th className="text-left py-2 px-3">Descrizione</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {[
                  ["users","Utenti (customer/business/admin) con email, ruolo, auth_provider"],
                  ["businesses","Attivita con geolocalizzazione, categoria, whatsapp, deposit"],
                  ["bookings","Prenotazioni con stato, pagamento, note cliente"],
                  ["availability","Regole disponibilita settimanali per attivita"],
                  ["menu_items","Menu digitale per pizzerie"],
                  ["notifications","Notifiche in-app + promemoria 15min/24h"],
                  ["reviews","Recensioni attivita (1-5 stelle)"],
                  ["satispay_payments","Pagamenti Satispay manuali"],
                  ["payment_transactions","Transazioni Stripe"],
                  ["user_sessions","Sessioni attive (cookie session_token)"],
                ].map(([c,d])=>(
                  <tr key={c}><td className="py-2 px-3 font-mono text-xs text-[#FF4A00] font-bold">{c}</td><td className="py-2 px-3 text-[#6A564A] text-xs">{d}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </Sec>

        {/* API ENDPOINTS */}
        <Sec icon={Code} title="API Endpoints" id="api">
          <div className="space-y-4 text-sm">
            {[
              { g: "Auth", eps: [
                ["POST","/api/auth/register","Registrazione"],
                ["POST","/api/auth/login","Login"],
                ["GET","/api/auth/me","Info utente"],
                ["POST","/api/auth/role","Scelta ruolo"],
                ["POST","/api/auth/logout","Logout"],
              ]},
              { g: "Attivita", eps: [
                ["GET","/api/businesses","Lista tutte"],
                ["GET","/api/businesses/search?lat=&lon=&cat=","Ricerca geo"],
                ["PUT","/api/businesses/{id}","Aggiorna (whatsapp, deposit...)"],
                ["POST","/api/businesses/register","Registra nuova"],
              ]},
              { g: "Prenotazioni", eps: [
                ["POST","/api/bookings","Crea prenotazione"],
                ["GET","/api/bookings/mine","Le mie"],
                ["GET","/api/bookings/business/{id}","Dell'attivita"],
                ["PUT","/api/bookings/{id}/status","Cambia stato"],
              ]},
              { g: "Disponibilita", eps: [
                ["GET","/api/availability/{biz_id}","Regole settimanali"],
                ["GET","/api/availability/{biz_id}/{date}","Slot per data"],
                ["POST","/api/availability","Imposta"],
              ]},
              { g: "Pagamenti", eps: [
                ["POST","/api/payments/checkout","Stripe checkout"],
                ["GET","/api/payments/success","Callback Stripe"],
                ["POST","/api/satispay/create-payment","Satispay manuale"],
                ["POST","/api/satispay/confirm/{id}","Conferma Satispay"],
              ]},
              { g: "Admin (God-Mode)", eps: [
                ["GET","/api/admin/stats","Statistiche piattaforma"],
                ["GET","/api/admin/users","Lista tutti utenti"],
                ["GET","/api/admin/transactions","Tutte le transazioni"],
                ["GET","/api/admin/users/{id}/bookings","Prenotazioni di un utente"],
                ["PUT","/api/admin/users/{id}/edit","Modifica profilo utente"],
                ["PUT","/api/admin/users/{id}/ban","Banna/sbanna utente"],
                ["DELETE","/api/admin/users/{id}","Elimina utente + dati"],
                ["GET","/api/admin/businesses/{id}/bookings","Prenotazioni di un'attivita"],
                ["PUT","/api/admin/bookings/{id}/status","Forza stato prenotazione"],
                ["DELETE","/api/admin/businesses/{id}","Elimina attivita"],
              ]},
            ].map(({g,eps})=>(
              <div key={g}>
                <p className="text-xs uppercase tracking-widest text-[#FF4A00] font-bold mb-1.5">{g}</p>
                {eps.map(([m,p,d])=>(
                  <div key={p} className="flex items-center gap-2 py-0.5">
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${m==="GET"?"bg-emerald-100 text-emerald-700":m==="POST"?"bg-blue-100 text-blue-700":m==="PUT"?"bg-amber-100 text-amber-700":"bg-red-100 text-red-700"}`}>{m}</span>
                    <C v={p} />
                    <span className="text-[#9CA3AF] text-xs hidden sm:inline">— {d}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </Sec>

        {/* PAGINE FRONTEND */}
        <Sec icon={Globe} title="Pagine Frontend" id="pagine">
          <div className="space-y-1">
            {[
              [`${appUrl}/`, "Landing page pubblica"],
              [`${appUrl}/admin`, "Pannello admin (god-mode)"],
              [`${appUrl}/docs`, "Questa documentazione"],
              [`${appUrl}/dashboard`, "Redirect automatico per ruolo"],
              [`${appUrl}/customer`, "Dashboard cliente"],
              [`${appUrl}/business`, "Dashboard attivita"],
              [`${appUrl}/business/register`, "Registrazione nuova attivita"],
              [`${appUrl}/book/:businessId`, "Pagina prenotazione (pubblica)"],
            ].map(([url, desc]) => (
              <div key={url} className="flex flex-col sm:flex-row sm:items-center gap-1 py-1.5 border-b border-slate-50 last:border-0">
                <C v={url} />
                <span className="text-[10px] text-[#9CA3AF] sm:ml-2">{desc}</span>
              </div>
            ))}
          </div>
        </Sec>

        {/* FILE PRINCIPALI */}
        <Sec icon={Globe} title="File Principali" id="files">
          <div className="space-y-1">
            {[
              ["/app/backend/server.py","API FastAPI completa (~3270 righe)"],
              ["/app/backend/.env","Variabili ambiente e chiavi API"],
              ["/app/backend/requirements.txt","Dipendenze Python"],
              ["/app/frontend/src/App.js","Router principale"],
              ["/app/frontend/src/pages/Landing.js","Homepage"],
              ["/app/frontend/src/pages/CustomerDashboard.js","Dashboard cliente"],
              ["/app/frontend/src/pages/BusinessDashboard.js","Dashboard attivita"],
              ["/app/frontend/src/pages/BusinessProfile.js","Pagina prenotazione"],
              ["/app/frontend/src/pages/AdminDashboard.js","Pannello admin"],
              ["/app/frontend/src/components/AuthModal.js","Login/Registrazione"],
              ["/app/frontend/src/components/WheelPicker.js","Selettore data/ora"],
              ["/app/frontend/src/contexts/AuthContext.js","Gestione sessione"],
              ["/app/frontend/src/contexts/LanguageContext.js","Traduzioni IT/EN/FR/DE/ES"],
              ["/app/HANDOFF_PROFESSIONISTA.md","Documentazione tecnica completa"],
              ["/app/MOBILE_APP_GUIDE.md","Guida pubblicazione app mobile"],
            ].map(([f,d])=>(
              <div key={f} className="flex flex-col sm:flex-row sm:items-center gap-1 py-1.5 border-b border-slate-50 last:border-0">
                <C v={f} />
                <span className="text-[10px] text-[#9CA3AF] sm:ml-2">{d}</span>
              </div>
            ))}
          </div>
        </Sec>

        {/* PRODUZIONE */}
        <Sec icon={Smartphone} title="Checklist Produzione" id="produzione">
          <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-xl">
            <ol className="list-decimal list-inside space-y-2 text-sm text-[#6A564A]">
              <li>Configurare <strong>dominio</strong> (es. bookeasy.it)</li>
              <li>Chiavi <strong>Stripe LIVE</strong> da <C v="https://dashboard.stripe.com" /></li>
              <li><strong>Google OAuth</strong> in <C v="https://console.cloud.google.com" /></li>
              <li><strong>Apple Sign In</strong> su <C v="https://developer.apple.com" /></li>
              <li>Server <strong>cloud</strong> (VPS, AWS, Hetzner)</li>
              <li><strong>MongoDB Atlas</strong> — <C v="https://cloud.mongodb.com" /></li>
              <li><strong>SSL</strong> con Let's Encrypt</li>
              <li>Chiave <strong>Resend</strong> per email — <C v="https://resend.com" /></li>
              <li>Pubblicare su <strong>App Store + Play Store</strong></li>
            </ol>
          </div>
        </Sec>

        <div className="text-center py-6">
          <Button
            onClick={copyAll}
            className="bg-[#09090B] text-white font-bold rounded-xl px-8 py-3 text-base border-2 border-[#09090B] hover:bg-[#FF4A00] transition-colors"
          >
            <ClipboardList className="h-5 w-5 mr-2" />
            COPIA TUTTO
          </Button>
          <p className="text-[10px] text-[#9CA3AF] mt-3">BookEasy — Generato automaticamente</p>
        </div>
      </div>
    </div>
  );
}
